import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Inbox } from "lucide-react";

export interface Column<T> {
  id: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: number | string;
  minWidth?: number;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  paper?: boolean;
  stickyHeader?: boolean;
  maxHeight?: number | string;
  onRowClick?: (row: T, index: number) => void;
  sx?: SxProps<Theme>;
}

function DataTable<T>({
  columns,
  data,
  keyExtractor,
  loading = false,
  emptyMessage = "No data found",
  emptyDescription,
  paper = true,
  stickyHeader = false,
  maxHeight,
  onRowClick,
  sx,
}: DataTableProps<T>) {
  const containerSx: SxProps<Theme> = [
    {
      borderRadius: 2,
      overflow: "hidden",
      ...(paper && {
        border: 1,
        borderColor: "divider",
      }),
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  const tableContainerProps = paper
    ? { component: Paper, variant: "outlined" as const, elevation: 0 }
    : {};

  return (
    <TableContainer
      {...tableContainerProps}
      sx={containerSx}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : data.length === 0 ? (
        <Stack sx={{ py: 8, px: 3 }} alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) =>
                theme.palette.mode === "light" ? "grey.100" : "action.hover",
              color: "text.secondary",
            }}
          >
            <Inbox size={24} />
          </Box>
          <Typography
            variant="body1"
            fontWeight={500}
            color="text.secondary"
          >
            {emptyMessage}
          </Typography>
          {emptyDescription && (
            <Typography variant="body2" color="text.secondary">
              {emptyDescription}
            </Typography>
          )}
        </Stack>
      ) : (
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  width={col.width}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "text.secondary",
                    bgcolor: (theme) =>
                      theme.palette.mode === "light"
                        ? "grey.50"
                        : "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                    whiteSpace: "nowrap",
                    ...(col.minWidth && { minWidth: col.minWidth }),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={keyExtractor(row, index)}
                hover
                onClick={
                  onRowClick ? () => onRowClick(row, index) : undefined
                }
                sx={{
                  ...(onRowClick && { cursor: "pointer" }),
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      py: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                      fontSize: "0.875rem",
                    }}
                  >
                    {col.render(row, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
}

export default DataTable;
